"use client";

import { type UseChatHelpers, useChat } from "@ai-sdk/react";
import { detectMessageLanguage } from "@repo/agent/language";
import { type ArabicDialect, arabicDialect } from "@repo/agent-config";
import {
	type AppearanceOverrides,
	type ChatStarter,
	type ChatText,
	CONTROL_CAPABILITIES,
	type EmbedSearch,
	type HostToPage,
	pickAppearanceOverrides,
	type RestrictableKind,
} from "@repo/embed";
import { ScriptOnce } from "@tanstack/react-router";
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@ui/components/alert-dialog";
import { Button } from "@ui/components/button";
import {
	Empty,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@ui/components/empty";
import {
	MessageScroller,
	MessageScrollerButton,
	MessageScrollerContent,
	MessageScrollerItem,
	MessageScrollerProvider,
	MessageScrollerViewport,
} from "@ui/components/message-scroller";
import {
	type ChatStatus,
	DefaultChatTransport,
	getToolName,
	isToolUIPart,
	type UIMessage,
} from "ai";
import { HeadsetIcon, RotateCcwIcon } from "lucide-react";
import {
	lazy,
	type ReactNode,
	Suspense,
	useCallback,
	useEffect,
	useEffectEvent,
	useMemo,
	useRef,
	useState,
} from "react";
import { type ShopperPageData, warmAgentFn } from "~/server/shopper";
import { BasketDrawer } from "./BasketDrawer";
import { BRIDGE_CAPABILITIES, legacyCartMessage } from "./bridge";
import { Composer } from "./Composer";
import {
	BasketBar,
	ChatHeader,
	composerPlaceholder,
	DeviceFrame,
	FollowUps,
	FooterLinks,
	useChatLayer,
} from "./chrome";
import { type Copy, copyFor, copyLanguage, fill } from "./copy";
import { CopyContext } from "./copy-context";
import { debugError, debugLog, setDebug, watchPage } from "./debug";
import {
	applyAppearanceOverrides,
	applyChatText,
	type ChatLabels,
	chatLabels,
} from "./overrides";
import { paintedScript } from "./painted";
import { RetailerMark } from "./RetailerMark";
import { loadMarkdown } from "./ShopperMarkdown";
import {
	AssistantTurn,
	type MessageContext,
	messageText,
	PendingText,
	ShopperMessage,
	type ShopperMessageMetadata,
	visibleParts,
} from "./ShopperMessage";
import { readStored, writeStored } from "./session";
import { nativeEmbed, startsHidden, type TurnState, useShown } from "./shown";
import { useOpenTiming } from "./timing";
import { type Basket, type ProductCard, productsFrom } from "./types";
import { useBasket } from "./use-basket";
import { useConversationEvents } from "./use-conversation-events";
import {
	type BridgePost,
	useBridgePost,
	useHostBridge,
} from "./use-host-bridge";
import { type ShopperSession, useShopperSession } from "./use-shopper-session";
import type { VoiceNote } from "./voice/hooks";
import { canCall, transcribeClip } from "./voice/speech";
import { Welcome } from "./Welcome";

// Only after a tap: kept out of the first load.
const LiveVoice = lazy(() =>
	import("./LiveVoice").then((m) => ({ default: m.LiveVoice })),
);
const ProductSheet = lazy(() =>
	import("./ProductSheet").then((m) => ({ default: m.ProductSheet })),
);
/**
 * "Help with an order" asks the assistant, which looks the shopper's orders up through the
 * retailer's order data (get_order_status) and hands over to the team when it cannot help.
 */
const ORDER_HELP = "I need help with an order";

/**
 * How long a restored conversation waits for the markdown renderer. Past it the chat opens with
 * the text as written and the renderer swaps in when it arrives: a stalled chunk must not hold
 * the skeleton, the composer and `ready`.
 */
const MARKDOWN_WAIT_MS = 1500;

/** A conversation as the server returns it. */
interface StoredConversation {
	id: string;
	messages: UIMessage[];
	basket: Basket;
	handoff: { requested: boolean; paused: boolean };
}

/** The server keeps a voice note's words; the waveform stays on this device. */
function readVoiceNotes(slug: string): Record<string, VoiceNote> {
	try {
		return JSON.parse(readStored("voice-notes", slug) ?? "{}");
	} catch {
		return {};
	}
}

const isArabicScript = (text: string) =>
	detectMessageLanguage(text).kind === "arabic";

export function ShopperChat({
	page: loaded,
	search,
	onLaunched,
	inline,
}: {
	page: ShopperPageData;
	search: EmbedSearch;
	/**
	 * The launch token or code is spent: off the URL, the session's store and locale in it.
	 * Stable: the session rebuilds its launch when it changes.
	 */
	onLaunched: (storeId: string | null, locale?: string) => void;
	/** Mounted inside one of our own pages (`InlineShopperChat`), not as the hosted page. */
	inline?: InlineMount;
}) {
	// What the app changed after the page loaded (`theme`, `appearance`, `text`), over the
	// published settings and the URL's overrides the server already applied.
	const [hostAppearance, setHostAppearance] = useState<AppearanceOverrides>(
		{},
	);
	const [hostText, setHostText] = useState<ChatText>({});
	// The app's suggestions (`starters`), language (`locale`) and restrictions (`restrictions`).
	const [hostStarters, setHostStarters] = useState<ChatStarter[] | null>(
		null,
	);
	const [hostLocale, setHostLocale] = useState<string | null>(null);
	const [hostRestrict, setHostRestrict] = useState<RestrictableKind[]>([]);
	// Read by every request the moment a message arrives: an app delivers its restrictions and
	// language and then a held chat.send back to back, before React has re-rendered.
	const sent = useRef<{
		restrict: RestrictableKind[];
		locale: string | null;
	}>({ restrict: [], locale: null });
	const page = useMemo(() => {
		const changed = loaded.appearance.allowAppOverrides
			? applyChatText(
					applyAppearanceOverrides(loaded, hostAppearance),
					hostText,
				)
			: loaded;
		// Shown unless Hephon allows this retailer to hide it, whatever the app says.
		return loaded.poweredByLocked && !changed.chat.poweredBy
			? { ...changed, chat: { ...changed.chat, poweredBy: true } }
			: changed;
	}, [loaded, hostAppearance, hostText]);

	// Where the page posts to the app: needs no session, so the session reports through it.
	const out = useBridgePost({
		allowedOrigins: page.allowedOrigins,
		parentOrigin: search.parentOrigin,
		embed: search.embed,
		inline: inline !== undefined,
	});
	const { post } = out;
	// Before any child's effect can log: set while rendering, the same values every time.
	// Each line also reaches the app as a `log` bridge message.
	setDebug(search.debug === 1, (level, message) =>
		post("log", { level, message }),
	);
	useEffect(() => watchPage(), []);

	const { showBasket } = page.chat;
	const [basketOpen, setBasketOpen] = useState(false);
	const [confirmReset, setConfirmReset] = useState(false);
	const [voiceNotice, setVoiceNotice] = useState(false);
	const [draft, setDraft] = useState("");
	const [liveOpen, setLiveOpen] = useState(false);
	const [openProduct, setOpenProduct] = useState<ProductCard | null>(null);
	// Until the stored conversation is read, a skeleton holds the middle rather than flashing
	// the welcome screen over a conversation that is about to load. Not on the launch screen.
	const [loading, setLoading] = useState(true);
	// A person from the retailer was asked for and has not written yet (ADR-9).
	const [teamJoining, setTeamJoining] = useState(false);
	const teamOnTheWay = useCallback(() => setTeamJoining(true), []);

	const voice = page.voice;
	// Whether this browser can hold a call is only known after mount.
	const [browserCanCall, setBrowserCanCall] = useState(false);
	useEffect(() => setBrowserCanCall(canCall()), []);
	const canGoLive = voice.live && browserCanCall;

	const sessionExpired = useCallback(
		() => post("session.expired", {}),
		[post],
	);
	const session = useShopperSession({
		page,
		search,
		onLaunched,
		onExpired: sessionExpired,
	});
	const { request, conversationId } = session;
	const restoring = loading && session.status !== "launch_required";
	const locale = useChatLocale(
		hostLocale,
		search.locale,
		session.launchLocale,
	);
	const dialect = arabicDialect(page.voice);
	const copy: Copy = copyFor(locale, dialect);
	const labels = useMemo(
		() => chatLabels(page.chat, page.retailerName, copy.labels),
		[page.chat, page.retailerName, copy],
	);
	/** What a new session opened by this request is for (ignored once one exists). */
	const opening = useMemo(
		() => ({
			storeId: session.storeId ?? undefined,
			embed: search.embed,
			sdk: search.sdk,
		}),
		[session.storeId, search.embed, search.sdk],
	);

	const transport = useMemo(
		() =>
			new DefaultChatTransport<UIMessage>({
				api: `/api/public/${page.orgSlug}/chat`,
				prepareSendMessagesRequest: ({ messages }) => {
					const last = messages.at(-1);
					const text = last ? messageText(last) : "";
					const current = conversationId();
					// A voice note reaches the agent as text like any other
					// turn; this is what tells the retailer's inbox it was said
					// out loud.
					const spoken = Boolean(
						(last?.metadata as ShopperMessageMetadata | undefined)
							?.voiceNote,
					);
					return {
						body: {
							...(current ? { conversationId: current } : {}),
							message: { id: last?.id, text },
							locale:
								sent.current.locale ??
								locale ??
								navigator.language,
							...(sent.current.restrict.length
								? { restrict: sent.current.restrict }
								: {}),
							...(spoken ? { via: "voice" as const } : {}),
							...opening,
						},
					};
				},
				// The session token and conversation id arrive as response headers
				// on the first turn; the session keeps them for every later request.
				fetch: (input, init) => request(String(input), init),
			}),
		[page.orgSlug, request, conversationId, opening, locale],
	);

	const {
		messages,
		setMessages,
		sendMessage,
		regenerate,
		stop,
		status,
		error,
		clearError,
		// Earlier turns are memoized; the streaming one still renders per
		// update, and 50 ms is smooth to read at a fraction of the work.
	} = useChat({ transport, experimental_throttle: 50 });
	const turn = turnOf(status, error);
	const busy = turn === "busy";
	// Settled, and no reply on its way.
	const idle = !busy && !restoring;

	const bridge = useHostBridge({
		out,
		messages,
		conversationId,
		onMessage: (message) => onHostMessage(message),
		onHandoff: teamOnTheWay,
	});
	useEffect(() => {
		debugLog(`chat: ${status}`);
	}, [status]);
	useEffect(() => {
		if (error) {
			debugError("chat error:", error);
		}
	}, [error]);

	const {
		basket,
		setBasket,
		restored: basketRestored,
		quantities,
		hostSnapshot,
		setQuantity,
		addAll,
		checkout: basketCheckout,
	} = useBasket({
		session,
		messages,
		idle,
		// What the first turn would have opened the conversation with.
		opening: () => ({
			...opening,
			...(sent.current.locale || locale
				? { locale: sent.current.locale ?? locale }
				: {}),
			...(sent.current.restrict.length
				? { restrict: sent.current.restrict }
				: {}),
		}),
		onDiff: bridge.cart,
	});

	const fetchConversation = useCallback(
		async (path: string) => {
			const res = await request(path);
			return res.ok ? ((await res.json()) as StoredConversation) : null;
		},
		[request],
	);

	/** Puts a conversation on screen, its voice-note bubbles restored from this device. */
	const showConversation = useCallback(
		(data: StoredConversation) => {
			const notes = readVoiceNotes(page.orgSlug);
			bridge.restored(data.messages);
			basketRestored(data.messages);
			setMessages(
				data.messages.map((message) =>
					notes[message.id]
						? {
								...message,
								metadata: { voiceNote: notes[message.id] },
							}
						: message,
				),
			);
			setBasket(data.basket);
			setTeamJoining(
				data.handoff.requested && !isStaff(data.messages.at(-1)),
			);
			session.setConversation(data.id);
		},
		[page.orgSlug, bridge, basketRestored, setMessages, setBasket, session],
	);

	/** A conversation from the server, on screen; false when there is none to read. */
	const loadConversation = useCallback(
		async (path: string) => {
			const data = await fetchConversation(path);
			if (data) {
				showConversation(data);
			}
			return data !== null;
		},
		[fetchConversation, showConversation],
	);

	/**
	 * Where this tab's conversation is read: its own, or (an identified shopper on another
	 * device) their latest one in this store. Null without a session to read it with.
	 */
	const conversationPath = () => {
		const current = conversationId();
		return current
			? `/conversations/${current}`
			: session.token()
				? "/conversations/latest"
				: null;
	};

	/** This tab's conversation on screen, when the session has one to read. */
	const readStoredConversation = async () => {
		const current = conversationId();
		const path = conversationPath();
		if (!path) {
			return;
		}
		// Its messages render the moment loading ends, so the markdown renderer downloads
		// next to the conversation and the conversation waits for it: no raw `**` on screen.
		const markdown = loadMarkdown();
		const found = await loadConversation(path);
		if (!found && current) {
			session.setConversation(null);
		}
		if (found) {
			await Promise.race([
				markdown,
				new Promise((resolve) => setTimeout(resolve, MARKDOWN_WAIT_MS)),
			]);
		}
	};
	const restoreConversation = useEffectEvent(() => {
		readStoredConversation()
			.catch(() => {})
			.finally(() => setLoading(false));
	});
	// A launch is a round trip before the conversation is: the renderer starts with it.
	useEffect(() => {
		if (search.lt || search.code) {
			void loadMarkdown();
		}
	}, [search.lt, search.code]);
	useEffect(() => {
		if (session.status === "ready") {
			restoreConversation();
		}
	}, [session.status]);

	// Tell the host (or the page an inline chat is in) we are here once the session is settled.
	const announced = useRef(false);
	const announce = useEffectEvent(() => {
		inline?.onReady();
		const current = conversationId();
		post("ready", {
			orgSlug: page.orgSlug,
			storeId: session.storeId ?? "",
			...(current ? { conversationId: current } : {}),
			...(session.hostSessionId
				? { hostSessionId: session.hostSessionId }
				: {}),
			// An app only restyles a chat, or sets its suggestions, where the retailer lets it.
			capabilities: [
				...BRIDGE_CAPABILITIES,
				...CONTROL_CAPABILITIES.filter(
					(c) =>
						c !== "starters" || page.appearance.allowAppOverrides,
				),
				...(page.appearance.allowAppOverrides
					? ["appearance", "text"]
					: []),
			],
		});
	});
	const native = nativeEmbed(search.embed);
	const startedHidden = startsHidden(search);
	const timing = useOpenTiming({
		native: native !== null,
		startedHidden,
		post,
		token: session.token,
		request,
	});
	useEffect(() => {
		if (restoring || announced.current) {
			return;
		}
		announced.current = true;
		const interactive = performance.now();
		announce();
		timing.interactive(interactive);
		// The welcome screen renders no markdown: its renderer waits for a quiet moment.
		(window.requestIdleCallback ?? setTimeout)(() => void loadMarkdown());
	}, [restoring, timing]);
	// The first timing waits for a session to be written on: a launch opens one before the
	// chat is usable, an anonymous chat with its first turn.
	// biome-ignore lint/correctness/useExhaustiveDependencies: a turn changing state is when a session can appear
	useEffect(() => timing.report(), [status, timing]);

	// What the shopper did in this document (a turn sent, a new conversation): a
	// conversation read before it no longer says what is on screen.
	const turns = useRef(0);
	// The shopper chose a new conversation here: their latest one on the server is the one
	// they just left.
	const startedOver = useRef(false);
	// The turn the shopper stopped: if it had said nothing yet, the notice says they stopped
	// it, not that it failed.
	const [stoppedTurn, setStoppedTurn] = useState<number | null>(null);
	const stopReply = () => {
		setStoppedTurn(turns.current);
		stop();
	};

	const visibility = useShown({
		startedHidden,
		turn,
		settled: !restoring,
		onHide: () => setLiveOpen(false),
		// The agent's connections were opened when the page loaded, maybe minutes ago.
		onShow: () => void warmAgentFn().catch(() => {}),
		// Also the session check: a 401 here asks the host for a new launch before the
		// shopper's first message.
		reload: () => {
			const path = conversationPath();
			if (!path) {
				return;
			}
			const turn = turns.current;
			// Asked all the same, for the session check; what it finds stays off screen.
			const fresh = startedOver.current && !conversationId();
			// A turn that failed unseen may never have reached the server.
			const failed = error
				? [...messages].reverse().find((m) => m.role === "user")
				: undefined;
			void fetchConversation(path)
				.then((data) => {
					// A turn sent while this was on its way is not in it: putting it on
					// screen would drop the shopper's message, bring back the basket from
					// before it and mark the reply's tool calls as already announced.
					if (!data || fresh || turns.current !== turn) {
						return;
					}
					showConversation(data);
					if (
						failed &&
						!data.messages.some((m) => m.id === failed.id)
					) {
						// Not saved: back in the composer rather than gone without a word.
						setDraft((draft) => draft || messageText(failed));
					}
				})
				.catch(() => {});
		},
		clearError,
	});
	const { shown } = visibility;

	/** Nothing while the chat is off screen: the app calls `focus()` after showing it. */
	const focusComposer = () => {
		if (!visibility.isShown()) {
			return;
		}
		requestAnimationFrame(() =>
			document
				.querySelector<HTMLTextAreaElement>(
					'textarea[aria-label="Message"]',
				)
				?.focus(),
		);
	};

	const onHostMessage = (message: HostToPage) => {
		switch (message.type) {
			case "cart.snapshot":
				hostSnapshot(message.data.items);
				break;
			case "session.launch":
				// The host minted a fresh token (after session.expired, or a
				// shopper who signed in): start over in that session.
				void session
					.launch({ launchToken: message.data.launchToken })
					.then((result) => {
						// Only a refused token asks for another: a rate limit or an
						// outage would refuse the next one too (the launch retried).
						if (result === "refused") {
							post("session.expired", {});
						}
						if (result !== "opened") {
							return;
						}
						stop();
						setMessages([]);
						setBasket(null);
						startedOver.current = false;
						const resumed = conversationId();
						if (resumed) {
							void loadConversation(`/conversations/${resumed}`);
						}
					});
				break;
			case "focus":
				focusComposer();
				break;
			case "visibility":
				visibility.set(message.data.shown);
				// `at` comes with the first delivery of a show: that is the open to time.
				if (message.data.shown && message.data.at !== undefined) {
					void timing.shown(message.data.at);
				}
				break;
			case "theme":
				setHostAppearance((current) => ({
					...current,
					scheme: message.data.scheme,
				}));
				break;
			case "appearance": {
				const set = pickAppearanceOverrides(message.data);
				setHostAppearance((current) => ({ ...current, ...set }));
				break;
			}
			case "text":
				setHostText((current) => ({
					...current,
					...message.data,
					labels: { ...current.labels, ...message.data.labels },
				}));
				break;
			case "chat.send":
				setBasketOpen(false);
				send(message.data.text);
				break;
			case "chat.new":
				startOver();
				break;
			case "basket.open":
				if (!showBasket) {
					debugLog(
						"basket.open ignored: the basket is off for this chat",
					);
				}
				setBasketOpen(showBasket);
				break;
			case "basket.close":
				setBasketOpen(false);
				break;
			case "starters":
				if (page.appearance.allowAppOverrides) {
					setHostStarters(
						message.data.items.length ? message.data.items : null,
					);
				}
				break;
			case "locale":
				sent.current.locale = message.data.locale;
				setHostLocale(message.data.locale);
				break;
			case "restrictions":
				sent.current.restrict = message.data.exclude;
				setHostRestrict(message.data.exclude);
				break;
		}
	};

	// Remember which messages were voice notes, so a reload keeps the bubble.
	// Written when a new one appears, not on every streamed token.
	const voiceNoteIds = messages
		.filter(
			(message) =>
				message.role === "user" &&
				(message.metadata as ShopperMessageMetadata | undefined)
					?.voiceNote,
		)
		.map((message) => message.id)
		.join(",");
	const saveVoiceNotes = useEffectEvent(() => {
		const notes = readVoiceNotes(page.orgSlug);
		for (const message of messages) {
			const note = (
				message.metadata as ShopperMessageMetadata | undefined
			)?.voiceNote;
			if (message.role === "user" && note) {
				notes[message.id] = { ...note, audioUrl: null };
			}
		}
		writeStored("voice-notes", page.orgSlug, JSON.stringify(notes));
	});
	useEffect(() => {
		if (voiceNoteIds) {
			saveVoiceNotes();
		}
	}, [voiceNoteIds]);

	/** Have the server search a message before it is sent, so the reply starts at once. */
	const prefetch = useCallback(
		(text: string) => {
			const current = conversationId();
			request("/prefetch", {
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({
					text,
					...(current ? { conversationId: current } : {}),
					...(opening.storeId ? { storeId: opening.storeId } : {}),
					...(sent.current.restrict.length
						? { restrict: sent.current.restrict }
						: {}),
				}),
				keepalive: true,
			}).catch(() => {});
		},
		[request, conversationId, opening],
	);

	// While the shopper types, search the draft once it rests (with what the page knows then).
	const prefetchDraft = useEffectEvent(prefetch);
	useEffect(() => {
		const text = draft.trim();
		if (text.length < 2 || text.length > 140) {
			return;
		}
		const timer = setTimeout(() => prefetchDraft(text), 350);
		return () => clearTimeout(timer);
	}, [draft]);

	const send = (text: string, metadata?: ShopperMessageMetadata) => {
		const trimmed = text.trim();
		if (!trimmed || busy) {
			return false;
		}
		clearError();
		turns.current += 1;
		sendMessage(metadata ? { text: trimmed, metadata } : { text: trimmed });
		return true;
	};

	useInlineAsk(inline, idle, send);

	/** A call opens only in front of the shopper. */
	const goLive = () => {
		if (visibility.isShown()) {
			setLiveOpen(true);
		}
	};

	/** The call button: the beta notice first, once per device (on every call it would be in the way). */
	const startCall = () => {
		if (!voice.betaNotice || readStored("voice-beta", page.orgSlug)) {
			goLive();
		} else {
			setVoiceNotice(true);
		}
	};

	const transcribe = useCallback(
		(recorded: Blob) =>
			transcribeClip(
				request,
				{ conversationId: conversationId(), ...opening },
				recorded,
			),
		[request, conversationId, opening],
	);

	/**
	 * A call writes its turns into the same conversation as the chat, so when
	 * it ends the transcript is simply reloaded: what was said on the call is
	 * there to scroll back through, with the same cards and the same basket.
	 */
	const endLive = (refocus: boolean) => {
		setLiveOpen(false);
		const current = conversationId();
		if (current) {
			void loadConversation(`/conversations/${current}`).catch(() => {});
		}
		if (refocus) {
			focusComposer();
		}
	};

	const sendVoiceNote = (note: VoiceNote) => {
		send(note.transcript, { voiceNote: note });
	};

	/** A new conversation in the same session (same shopper, same store). */
	const startOver = () => {
		stop();
		turns.current += 1;
		startedOver.current = true;
		session.setConversation(null);
		setTeamJoining(false);
		setMessages([]);
		setBasket(null);
		clearError();
		writeStored("voice-notes", page.orgSlug, null);
	};

	/** "host": the retailer's app took the cart; "standalone": no app around it. */
	const checkout = async (): Promise<"host" | "standalone" | "failed"> => {
		const result = await basketCheckout();
		if (!result) {
			return "failed";
		}
		if (!bridge.embedded()) {
			debugLog("checkout: no app around the chat to hand the cart to");
			return "standalone";
		}
		post("checkout", result.cart, result.eventId ?? undefined);
		bridge.postLegacy(legacyCartMessage(result.cart));
		setBasketOpen(false);
		return "host";
	};

	/** The product sheet opens here; the host may also want to show its own page. */
	const showProduct = useCallback(
		(product: ProductCard) => {
			// Off for an app that shows its own product page from `open_product`.
			if (page.chat.productSheet) {
				setOpenProduct(product);
			}
			if (session.storeId) {
				post("open_product", {
					itemId: product.itemId,
					catalogId: product.externalId,
					storeId: session.storeId,
				});
			}
		},
		[post, session.storeId, page.chat.productSheet],
	);

	const openSupport = page.support.enabled ? () => send(ORDER_HELP) : null;

	useStaffEvents({
		shown,
		messages,
		busy,
		conversationId,
		request,
		loadConversation,
		setMessages,
		setTeamJoining,
	});

	// Stable between tokens, so the memoized earlier turns stay put.
	const context: MessageContext = useMemo(
		() => ({
			assistant: {
				name: page.assistantName,
				retailerName: page.retailerName,
				logo: page.logo,
				showLogo: page.chat.showLogo,
				showName: page.chat.showAssistantName,
			},
			status: { thinking: labels.thinking, typing: labels.typing },
			showSteps: page.chat.showSteps,
			addable: page.chat.addFromCards,
			quantities,
			onQuantity: setQuantity,
			onAddAll: addAll,
			onFixTranscript: setDraft,
			onOpenProduct: showProduct,
			shown,
		}),
		[
			page.assistantName,
			page.retailerName,
			page.logo,
			page.chat.showLogo,
			page.chat.showAssistantName,
			labels.thinking,
			labels.typing,
			page.chat.showSteps,
			page.chat.addFromCards,
			quantities,
			setQuantity,
			addAll,
			showProduct,
			shown,
		],
	);

	const reply = replyView({
		messages,
		turn,
		stopped: stoppedTurn === turns.current,
		basket,
		dialect,
		followUps: page.chat.followUps,
		liveOpen,
		teamJoining,
	});

	const welcome = !restoring && messages.length === 0;
	const itemCount = basket?.itemCount ?? 0;
	const drawerOpen = showBasket && basketOpen;
	useStateReport({
		post,
		restoring,
		welcome,
		busy,
		basketOpen: drawerOpen,
		itemCount,
		hasMessages: messages.length > 0,
		conversationId,
	});

	const frame = {
		appearance: page.appearance,
		customFont: page.appearance.customFont,
		native: native !== null,
		inline: inline !== undefined,
	};
	if (session.status === "launch_required") {
		return (
			<DeviceFrame {...frame}>
				<CopyContext value={copy}>
					<LaunchRequired page={page} copy={copy} />
				</CopyContext>
			</DeviceFrame>
		);
	}

	return (
		<DeviceFrame {...frame} dir={textDirection(locale)}>
			<CopyContext value={copy}>
				<ChatTop
					page={page}
					widget={search.embed === "widget"}
					divided={!welcome}
					labels={labels}
					onSupport={openSupport}
					canStartOver={messages.length > 0}
					onNewChat={() =>
						itemCount ? setConfirmReset(true) : startOver()
					}
					itemCount={itemCount}
					onBasket={() => setBasketOpen(true)}
					onClose={() => post("close", {})}
				/>

				<ChatMiddle
					restoring={restoring}
					welcome={welcome}
					showWelcome={page.chat.showWelcome}
					identity={context.assistant}
					welcomeScreen={
						<Welcome
							page={page}
							appStarters={hostStarters}
							restrict={hostRestrict}
							welcomeTitle={copy.welcomeTitle}
							onPick={send}
							onPress={prefetch}
							onSupport={openSupport}
							supportLabel={labels.orderHelp}
						/>
					}
				>
					<Conversation
						messages={messages}
						busy={busy}
						reply={reply}
						context={context}
						teamJoining={teamJoining}
						retailerName={page.retailerName}
						copy={copy}
						labels={labels}
						onPick={send}
						onPress={prefetch}
						onRetry={() => {
							clearError();
							turns.current += 1;
							regenerate();
						}}
					/>
				</ChatMiddle>

				<ChatFooter
					page={page}
					labels={labels}
					basket={basket}
					welcome={welcome}
					onOpenBasket={() => setBasketOpen(true)}
				>
					<Composer
						draft={draft}
						setDraft={setDraft}
						busy={busy}
						shown={shown}
						onSend={(text) => send(text)}
						onStop={stopReply}
						onVoiceNote={sendVoiceNote}
						placeholder={composerPlaceholder(page, copy)}
						voice={voice}
						transcribe={transcribe}
						onLive={canGoLive ? startCall : null}
					/>
				</ChatFooter>
				{/* Right after the chat's markup: the host drops its spinner when this runs. */}
				{native ? (
					<ScriptOnce>{paintedScript(native)}</ScriptOnce>
				) : null}

				<BasketDrawer
					labels={labels}
					showCheckout={page.chat.showCheckout}
					open={drawerOpen}
					onOpenChange={setBasketOpen}
					basket={basket}
					retailerName={page.retailerName}
					onQuantity={setQuantity}
					onCheckout={checkout}
				/>

				<Suspense fallback={null}>
					{liveOpen ? (
						<LiveVoice
							orgSlug={page.orgSlug}
							retailerName={page.retailerName}
							logo={page.logo}
							assistantName={page.assistantName}
							call={liveCall({
								session,
								search,
								sent: sent.current,
								locale,
							})}
							basket={basket}
							quantities={quantities}
							onQuantity={setQuantity}
							onOpenProduct={showProduct}
							onBasket={setBasket}
							onToolResult={bridge.toolResult}
							onConversation={session.setConversation}
							onSession={session.adopt}
							onSessionEnded={session.expire}
							onClose={endLive}
						/>
					) : null}

					{openProduct ? (
						<ProductSheet
							addLabel={labels.addToBasket}
							product={openProduct}
							quantity={quantities.get(openProduct.id) ?? 0}
							onQuantity={setQuantity}
							onSimilar={(product) => {
								setOpenProduct(null);
								send(
									fill(copy.alternativesTo, {
										title: product.title,
									}),
								);
							}}
							onClose={() => setOpenProduct(null)}
						/>
					) : null}
				</Suspense>

				<ChatAlert
					open={voiceNotice}
					onOpenChange={setVoiceNotice}
					title={copy.voiceBetaTitle}
					description={copy.voiceBetaBody}
					cancel={copy.notNow}
				>
					<Button
						onClick={() => {
							writeStored("voice-beta", page.orgSlug, "seen");
							setVoiceNotice(false);
							goLive();
						}}
					>
						{copy.startTalking}
					</Button>
				</ChatAlert>

				<ChatAlert
					open={confirmReset}
					onOpenChange={setConfirmReset}
					title={copy.newChatTitle}
					description={copy.newChatBody}
					cancel={copy.keepChatting}
				>
					<Button
						variant="destructive"
						onClick={() => {
							startOver();
							setConfirmReset(false);
						}}
					>
						{copy.startOver}
					</Button>
				</ChatAlert>
			</CopyContext>
		</DeviceFrame>
	);
}

/**
 * The chat as a component of one of our own pages (the public site's demo store): the same
 * chat, its session on this origin, with no app around it. `ask` sends a message for the
 * shopper once the chat is ready, again whenever its `id` changes; `onReady` says the chat is
 * on screen and usable. Its theme stays on its own element (`DeviceFrame` `inline`), so it fills
 * whatever box it is put in.
 */
export function InlineShopperChat({
	page,
	ask = null,
	onReady = NO_CALLBACK,
}: {
	page: ShopperPageData;
	ask?: ChatAsk | null;
	onReady?: () => void;
}) {
	return (
		<ShopperChat
			page={page}
			search={NO_SEARCH}
			onLaunched={NO_CALLBACK}
			inline={{ ask, onReady }}
		/>
	);
}

/** A message sent for the shopper: `id` changes on every ask, so the same words can go twice. */
export interface ChatAsk {
	id: number;
	text: string;
}

interface InlineMount {
	ask: ChatAsk | null;
	onReady: () => void;
}

/**
 * What the page around an inline chat asks for the shopper (a framed chat gets the bridge's
 * `chat.send`): sent once the chat is `idle` (settled, no reply on its way), each ask once.
 */
function useInlineAsk(
	inline: InlineMount | undefined,
	idle: boolean,
	send: (text: string) => boolean,
) {
	const ask = inline?.ask;
	const asked = useRef<number | null>(null);
	const sendAsk = useEffectEvent((next: ChatAsk) => {
		if (send(next.text)) {
			asked.current = next.id;
		}
	});
	useEffect(() => {
		if (ask && idle && asked.current !== ask.id) {
			sendAsk(ask);
		}
	}, [ask, idle]);
}

const NO_SEARCH: EmbedSearch = {};
const NO_CALLBACK = () => {};

/** A confirmation over the chat, inside it when it is mounted inline. */
function ChatAlert({
	open,
	onOpenChange,
	title,
	description,
	cancel,
	children,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	description: string;
	cancel: string;
	/** The action. */
	children: ReactNode;
}) {
	const layer = useChatLayer();
	return (
		<AlertDialog open={open} onOpenChange={onOpenChange}>
			<AlertDialogContent container={layer ?? undefined}>
				<AlertDialogHeader>
					<AlertDialogTitle>{title}</AlertDialogTitle>
					<AlertDialogDescription>
						{description}
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel>{cancel}</AlertDialogCancel>
					{children}
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}

/** The chat's status as the page reads it: a reply on its way, a failed turn, or nothing. */
function turnOf(status: ChatStatus, error: Error | undefined): TurnState {
	if (status === "submitted" || status === "streaming") {
		return "busy";
	}
	return error ? "error" : "idle";
}

/** Right to left when the chat's own wording is Arabic. */
function textDirection(locale: string | null): "rtl" | undefined {
	return copyLanguage(locale) === "ar" ? "rtl" : undefined;
}

/**
 * The app's language: the first one given (its `locale` message, the URL, the launch token),
 * else the device's, which is only known after mount (the server rendered without it).
 */
function useChatLocale(
	...given: Array<string | null | undefined>
): string | null {
	const [deviceLocale, setDeviceLocale] = useState<string | null>(null);
	useEffect(() => setDeviceLocale(navigator.language), []);
	return given.find((locale) => locale != null) ?? deviceLocale;
}

/**
 * Staff replies and the end of a handoff, while a conversation is open and on screen (a hidden
 * chat holds no stream; showing it reloads the conversation).
 */
function useStaffEvents({
	shown,
	messages,
	busy,
	conversationId,
	request,
	loadConversation,
	setMessages,
	setTeamJoining,
}: {
	shown: boolean;
	messages: UIMessage[];
	busy: boolean;
	conversationId: () => string | null;
	request: ShopperSession["request"];
	loadConversation: (path: string) => Promise<boolean>;
	setMessages: UseChatHelpers<UIMessage>["setMessages"];
	setTeamJoining: (joining: boolean) => void;
}) {
	const openConversation =
		shown && messages.length > 0 ? conversationId() : null;
	useConversationEvents({
		conversationId: openConversation,
		request,
		onEvent: (event) => {
			switch (event.type) {
				case "ready":
					setTeamJoining(
						event.requested && !isStaff(messages.at(-1)),
					);
					return;
				case "message":
					setTeamJoining(false);
					setMessages((current) =>
						current.some((m) => m.id === event.message.id)
							? current
							: [
									...current,
									{
										id: event.message.id,
										role: "assistant",
										parts: [
											{
												type: "text",
												text: event.message.text,
											},
										],
										metadata: {
											staff: {
												name: event.message.authorName,
											},
										} satisfies ShopperMessageMetadata,
									},
								],
					);
					return;
				case "handoff_released":
					setTeamJoining(false);
					return;
			}
		},
		// Pub/sub keeps no backlog: after a drop, what was missed comes from the server.
		onReconnect: () => {
			if (openConversation && !busy) {
				void loadConversation(
					`/conversations/${openConversation}`,
				).catch(() => {});
			}
		},
	});
}

/** What the chat is doing, for an app whose own header follows it (`state`). */
function useStateReport({
	post,
	restoring,
	welcome,
	busy,
	basketOpen,
	itemCount,
	hasMessages,
	conversationId,
}: {
	post: BridgePost["post"];
	restoring: boolean;
	welcome: boolean;
	busy: boolean;
	basketOpen: boolean;
	itemCount: number;
	hasMessages: boolean;
	conversationId: () => string | null;
}) {
	const current = hasMessages ? conversationId() : null;
	useEffect(() => {
		if (restoring) {
			return;
		}
		post("state", {
			view: welcome ? "welcome" : "conversation",
			busy,
			basketOpen,
			itemCount,
			...(current ? { conversationId: current } : {}),
		});
	}, [post, restoring, welcome, busy, basketOpen, itemCount, current]);
}

/**
 * The end of the conversation: the reply's placeholder, the notice and retry after a turn that
 * failed, came back empty or was stopped before a word, and two or three suggested replies.
 */
function replyView({
	messages,
	turn,
	stopped,
	basket,
	dialect,
	followUps: offered,
	liveOpen,
	teamJoining,
}: {
	messages: UIMessage[];
	turn: TurnState;
	/** The shopper stopped the latest turn. */
	stopped: boolean;
	basket: Basket | null;
	dialect: ArabicDialect;
	/** `chat.followUps`. */
	followUps: boolean;
	liveOpen: boolean;
	teamJoining: boolean;
}) {
	const busy = turn === "busy";
	const lastMessage = messages.at(-1);
	// Sent, and no reply message yet: the reply's own row takes over
	// (with its status and placeholder lines) as soon as it exists.
	const thinking = busy && lastMessage?.role === "user";
	// A turn that ended without a word or a result (the model returned
	// nothing, the stream died): offer the retry, never a blank row.
	const lastMeta = lastMessage?.metadata as
		| ShopperMessageMetadata
		| undefined;
	// Paused (a person holds the chat) or a person's reply: no retry, no suggestions.
	const peopleTurn = Boolean(lastMeta?.agentPaused || lastMeta?.staff);
	const emptyReply =
		!busy &&
		!peopleTurn &&
		lastMessage?.role === "assistant" &&
		visibleParts(lastMessage, true).length === 0;
	const stoppedEarly =
		!busy && stopped && (emptyReply || lastMessage?.role === "user");
	const lastUser = [...messages].reverse().find((m) => m.role === "user");
	const lastUserText = lastUser ? messageText(lastUser) : "";
	const suggestions =
		offered &&
		turn === "idle" &&
		!emptyReply &&
		lastMessage?.role === "assistant" &&
		!liveOpen &&
		!peopleTurn &&
		!teamJoining
			? followUps(lastMessage, basket, lastUserText, dialect)
			: [];
	return {
		thinking,
		stoppedEarly,
		/** The notice and the retry. */
		problem: !busy && (turn === "error" || emptyReply || stoppedEarly),
		lastUserText,
		suggestions,
	};
}

type ReplyView = ReturnType<typeof replyView>;

/** What a call opens with: this page's session, conversation, store and language. */
function liveCall({
	session,
	search,
	sent,
	locale,
}: {
	session: ShopperSession;
	search: EmbedSearch;
	sent: { restrict: RestrictableKind[]; locale: string | null };
	locale: string | null;
}) {
	return {
		session: session.token(),
		conversationId: session.conversationId(),
		store: session.storeId,
		embed: search.embed,
		sdk: search.sdk,
		locale: sent.locale ?? locale ?? undefined,
		restrict: sent.restrict,
	};
}

/** The header, or only the safe area when the retailer hides it. */
function ChatTop({
	page,
	widget,
	divided,
	labels,
	onSupport,
	canStartOver,
	onNewChat,
	itemCount,
	onBasket,
	onClose,
}: {
	page: ShopperPageData;
	/** The widget keeps its header whatever is set: the close button lives there. */
	widget: boolean;
	divided: boolean;
	labels: ChatLabels;
	onSupport: (() => void) | null;
	/** There is a conversation to leave. */
	canStartOver: boolean;
	onNewChat: () => void;
	itemCount: number;
	onBasket: () => void;
	onClose: () => void;
}) {
	if (!page.chat.showHeader && !widget) {
		return (
			<div className="shrink-0 bg-(--shop-surface) pt-(--shop-safe-top)" />
		);
	}
	const menu = [
		...(onSupport
			? [
					{
						label: labels.orderHelp,
						icon: <HeadsetIcon />,
						onSelect: onSupport,
					},
				]
			: []),
		...(canStartOver && page.chat.showNewChat
			? [
					{
						label: labels.newChat,
						icon: <RotateCcwIcon />,
						onSelect: onNewChat,
					},
				]
			: []),
	];
	return (
		<ChatHeader
			page={page}
			divided={divided}
			menu={menu}
			itemCount={itemCount}
			onBasket={
				page.chat.showBasket && page.chat.showBasketButton
					? onBasket
					: null
			}
			onBack={page.chat.showBack && !widget ? onClose : null}
			onClose={widget ? onClose : null}
		/>
	);
}

/** The middle: a skeleton while the conversation loads, the welcome screen, or the conversation. */
function ChatMiddle({
	restoring,
	welcome,
	showWelcome,
	identity,
	welcomeScreen,
	children,
}: {
	restoring: boolean;
	/** No conversation yet. */
	welcome: boolean;
	/** `chat.showWelcome`: off, the app draws its own first screen above the chat. */
	showWelcome: boolean;
	identity: MessageContext["assistant"];
	welcomeScreen: ReactNode;
	/** The conversation. */
	children: ReactNode;
}) {
	if (restoring) {
		return (
			<div className="min-h-0 flex-1" aria-busy="true">
				<div className="mx-auto w-full max-w-2xl px-4 pt-5">
					<AssistantTurn identity={identity} status={null} still>
						<PendingText />
					</AssistantTurn>
				</div>
			</div>
		);
	}
	if (!welcome) {
		return children;
	}
	return showWelcome ? welcomeScreen : <div className="min-h-0 flex-1" />;
}

/** The messages, then what follows the latest one: a person joining, the placeholder, suggestions, a retry. */
function Conversation({
	messages,
	busy,
	reply,
	context,
	teamJoining,
	retailerName,
	copy,
	labels,
	onPick,
	onPress,
	onRetry,
}: {
	messages: UIMessage[];
	busy: boolean;
	reply: ReplyView;
	context: MessageContext;
	teamJoining: boolean;
	retailerName: string;
	copy: Copy;
	labels: ChatLabels;
	onPick: (text: string) => boolean;
	onPress: (text: string) => void;
	onRetry: () => void;
}) {
	// Cards show Arabic names under an Arabic-script message.
	let arabicTurn = false;
	return (
		<MessageScrollerProvider autoScroll defaultScrollPosition="last-anchor">
			<MessageScroller className="min-h-0 flex-1">
				<MessageScrollerViewport aria-label="Conversation">
					<MessageScrollerContent className="@container mx-auto w-full max-w-2xl gap-5 px-4 pt-5 pb-6">
						{messages.map((message, index) => {
							if (message.role === "user") {
								arabicTurn = isArabicScript(
									messageText(message),
								);
							}
							return (
								<MessageScrollerItem
									key={message.id}
									messageId={message.id}
									scrollAnchor={message.role === "user"}
								>
									<ShopperMessage
										message={message}
										complete={
											index < messages.length - 1 || !busy
										}
										arabic={arabicTurn}
										context={context}
									/>
								</MessageScrollerItem>
							);
						})}
						{teamJoining && !busy ? (
							<TeamJoining
								retailerName={retailerName}
								copy={copy}
							/>
						) : null}
						{reply.thinking ? (
							<AssistantTurn
								identity={context.assistant}
								status={labels.thinking}
							>
								<PendingText />
							</AssistantTurn>
						) : null}
						{reply.suggestions.length ? (
							<FollowUps
								suggestions={reply.suggestions}
								rtl={isArabicScript(reply.lastUserText)}
								onPick={onPick}
								onPress={onPress}
							/>
						) : null}
						{reply.problem ? (
							<div
								role="alert"
								className="flex flex-col items-start gap-2.5"
							>
								<p className="text-(--shop-ink) text-[15.5px] leading-relaxed">
									{reply.stoppedEarly
										? copy.stopped
										: copy.noAnswer}
								</p>
								<Button
									variant="outline"
									size="sm"
									className="rounded-(--shop-btn)"
									onClick={onRetry}
								>
									<RotateCcwIcon data-icon="inline-start" />
									{labels.tryAgain}
								</Button>
							</div>
						) : null}
					</MessageScrollerContent>
				</MessageScrollerViewport>
				<MessageScrollerButton className="size-10 rounded-(--shop-btn) bg-(--shop-card) text-(--shop-ink) shadow-[0_6px_18px_-6px_rgb(0_0_40/0.35)] ring-1 ring-(--shop-brand-line) hover:bg-(--shop-card) [&_svg]:size-4.5" />
			</MessageScroller>
		</MessageScrollerProvider>
	);
}

/** The basket bar over the composer, and the links under the welcome screen. */
function ChatFooter({
	page,
	labels,
	basket,
	welcome,
	onOpenBasket,
	children,
}: {
	page: ShopperPageData;
	labels: ChatLabels;
	basket: Basket | null;
	welcome: boolean;
	onOpenBasket: () => void;
	/** The composer. */
	children: ReactNode;
}) {
	return (
		<footer className="shrink-0 bg-(--shop-surface) px-3 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
			<div className="mx-auto flex w-full max-w-2xl flex-col gap-2">
				{page.chat.showBasket &&
				page.chat.showBasketBar &&
				basket?.itemCount &&
				!welcome ? (
					<BasketBar
						basket={basket}
						label={labels.viewBasket}
						onOpen={onOpenBasket}
					/>
				) : null}
				{children}
				{welcome ? (
					<FooterLinks
						privacyUrl={page.privacyUrl}
						poweredBy={page.chat.poweredBy}
					/>
				) : null}
			</div>
		</footer>
	);
}

/** A message written by a person on the retailer's team. */
function isStaff(message: UIMessage | undefined): boolean {
	return Boolean(
		(message?.metadata as ShopperMessageMetadata | undefined)?.staff,
	);
}

/** While a person from the retailer is on the way: the agent handed the chat over. */
function TeamJoining({
	retailerName,
	copy,
}: {
	retailerName: string;
	copy: Copy;
}) {
	return (
		<div
			role="status"
			className="flex animate-shop-rise items-center gap-3 rounded-[calc(var(--radius)*2.75)] bg-(--shop-card) px-4 py-3 ring-1 ring-(--shop-brand-line)"
		>
			<span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-(--shop-accent-soft) text-(--shop-accent-ink)">
				<HeadsetIcon className="size-4.5" aria-hidden />
			</span>
			<span className="min-w-0 leading-tight">
				<span className="block font-bold text-(--shop-ink) text-[14.5px]">
					{copy.teamJoining}
				</span>
				<span className="block text-(--shop-muted) text-[13px]">
					{fill(copy.teamWillReply, { retailer: retailerName })}
				</span>
			</span>
		</div>
	);
}

/**
 * The retailer only opens chats from its own app (launch tokens), and this visit came
 * without one. Nothing to do here, so nothing else on screen.
 */
function LaunchRequired({ page, copy }: { page: ShopperPageData; copy: Copy }) {
	return (
		<div className="flex min-h-0 flex-1 flex-col items-center justify-center px-6 pt-(--shop-safe-top)">
			<Empty className="border-0">
				<EmptyHeader>
					<EmptyMedia>
						<RetailerMark
							name={page.retailerName}
							logo={page.logo}
							className="size-14 text-lg"
						/>
					</EmptyMedia>
					<EmptyTitle className="text-(--shop-ink)">
						{fill(copy.launchRequired, {
							retailer: page.retailerName,
						})}
					</EmptyTitle>
				</EmptyHeader>
			</Empty>
		</div>
	);
}

/**
 * Two or three next steps under the latest reply, in the shopper's own
 * register: English, Arabizi or Arabic script. Chosen from what the reply
 * did (showed products, a recipe, nothing) and whether the basket has items.
 */
function followUps(
	reply: UIMessage,
	basket: Basket | null,
	shopperText: string,
	dialect: ArabicDialect,
): string[] {
	const tools = reply.parts
		.filter(isToolUIPart)
		.map((part) => getToolName(part));
	if (tools.includes("escalate_to_human")) {
		return [];
	}
	// The same reading of the language as the agent's own (language.ts).
	const { kind } = detectMessageLanguage(shopperText);
	const register =
		kind === "arabic" ? "ar" : kind === "arabizi" ? "arabizi" : "en";
	const say =
		dialect === "gulf"
			? GULF_FOLLOW_UPS
			: {
					cheaper: {
						en: "Anything cheaper?",
						arabizi: "Fi shi arkhas?",
						ar: "في شي أرخص؟",
					},
					offers: {
						en: "Any of these on offer?",
						arabizi: "Fi 3orod 3layon?",
						ar: "في عروض عليهن؟",
					},
					more: {
						en: "Show me other options",
						arabizi: "Farjine ghayron",
						ar: "فرجيني غيرهن",
					},
					bigger: {
						en: "Make it for 2 more people",
						arabizi: "Zid la shakhsen kamen",
						ar: "زيد الكمية لشخصين كمان",
					},
					today: {
						en: "What's on offer today?",
						arabizi: "Shou l 3orod l yom?",
						ar: "شو العروض اليوم؟",
					},
					basket: {
						en: "What's in my basket?",
						arabizi: "Shou fi bel basket?",
						ar: "شو في بالسلة؟",
					},
				};
	const shownProducts = reply.parts.some(
		(part) =>
			isToolUIPart(part) &&
			part.state === "output-available" &&
			productsFrom(part.output).length > 0,
	);
	const picks: Array<keyof typeof say> = shownProducts
		? [
				"cheaper",
				tools.includes("find_ingredients") ? "bigger" : "more",
				"offers",
			]
		: ["today"];
	if (basket?.itemCount && picks.length < 3) {
		picks.push("basket");
	}
	const asked = plain(shopperText);
	return picks
		.map((key) => say[key][register])
		.filter((text) => plain(text) !== asked)
		.slice(0, 3);
}

/** The suggested replies of a retailer whose Arabic is Gulf (`arabicDialect`). */
const GULF_FOLLOW_UPS = {
	cheaper: {
		en: "Anything cheaper?",
		arabizi: "Fi shay arkhas?",
		ar: "فيه شي أرخص؟",
	},
	offers: {
		en: "Any of these on offer?",
		arabizi: "Fi 3orood 3alaiha?",
		ar: "فيه عروض عليها؟",
	},
	more: {
		en: "Show me other options",
		arabizi: "Warrini khayarat thanya",
		ar: "ورّني خيارات ثانية",
	},
	bigger: {
		en: "Make it for 2 more people",
		arabizi: "Zawwid el kamiya l shakhsain zyada",
		ar: "زوّد الكمية لشخصين زيادة",
	},
	today: {
		en: "What's on offer today?",
		arabizi: "Wesh el 3orood el yom?",
		ar: "وش العروض اليوم؟",
	},
	basket: {
		en: "What's in my basket?",
		arabizi: "Wesh fi sallati?",
		ar: "وش في سلتي؟",
	},
};

/** Letters and digits only, for "is this the same thing they just asked". */
function plain(text: string): string {
	return text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
}
